import { Clear, Delete, Edit, EventRepeatTwoTone, InfoSharp, LocalOffer, Schedule, QuestionAnswer, Search as SearchIcon } from '@mui/icons-material';
import { Button, IconButton, InputAdornment, TablePagination, TextField } from '@mui/material';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import { useContext, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import constants from '../config/constants';
import { AlertContext } from '../context/alert.context';
import { useModal } from '../context/ModalProvider';
import { IMember, IPrivateService } from '../types';
import { getLocalizedErrorMessage } from '../utils/functions';
import { format } from 'date-fns';
export type IPrivateServiceAppointment = {
    time: Date;
    service: IPrivateService;
    _id: string;
    followup?: string;
    followup_of?: string;
    price?: number;
    member: IMember;
    first_name: string;
    last_name: string;
    payment_confirm: boolean;
    cancellable: boolean;
    subsidized: boolean;
    duration: number;
    email: string;
    active: boolean;
    phone: string;
    schedule_another: boolean;
    host: string;
    coupon?: string | {
        _id: string;
        code: string;
        discountType: 'fixed' | 'percent';
        discountValue: number;
    };
};
type PrivateSessionTableRowProps = {
    session: IPrivateServiceAppointment;
    onPrivateSessionSetAppointment: (member_id: string, member_name: string, service: any) => void;
    // GH-539/625: open the "several future appointments" dialog (deferred payment, min 2).
    onScheduleFutureAppointments: (member_id: string, member_name: string, service: any) => void;
    refetchFunction: () => Promise<void>;
    pairSessionNumber?: number; // session N in the (member, therapist) pair
    // GH-715: click on the name → popup with every session of this member (chronological).
    onShowMemberSessions: (session: IPrivateServiceAppointment) => void;
};

const Row = ({ session, onPrivateSessionSetAppointment, onScheduleFutureAppointments, refetchFunction, pairSessionNumber, onShowMemberSessions }: PrivateSessionTableRowProps) => {
    const alert = useContext(AlertContext);
    const modal = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const handleCancelSession = async (session_id: string) => {
        try {
            setIsLoading(true);
            modal.hide();
            await axios.delete(`${constants.API_URL}/member/private_service/appointment/`, {
                data: { slot_id: session_id, silent: false },
            });
            alert?.showAlert('המפגש בוטל', 'success');

            await refetchFunction();
        } catch (error) {
            alert?.showAlert(getLocalizedErrorMessage(error as AxiosError, 'he'), 'error');
        } finally {
            setIsLoading(false);
        }
    };
    const handleRescheduleSession = async (session_id: string, new_time: Date) => {
        try {
            setIsLoading(true);
            modal.hide();
            await axios.put(`${constants.API_URL}/instructor/admin/private_session`, {
                new_time: new_time,
                old_slot_id: session_id,
            });
            alert?.showAlert('המפגש עודכן', 'success');

            await refetchFunction();
        } catch (error) {
            alert?.showAlert(getLocalizedErrorMessage(error as AxiosError, 'he'), 'error');
        } finally {
            setIsLoading(false);
        }
    };
    const showCancelDialog = async (session_id: string) => {
        modal.show({
            title: 'בטוח/ה?',
            body: 'האם את/ה בטוח/ה שברצונך לבטל את הפגישה?',
            footer: (
                <div className='flex flex-row justify-between w-full'>
                    <Button onClick={() => handleCancelSession(session_id)} color='error'>
                        כן
                    </Button>
                    <Button onClick={modal.hide}>לא</Button>
                </div>
            ),
        });
    };
    const showRescheduleModal = (session_id: string, session: IPrivateServiceAppointment) => {
        let new_time: Date | null = null;
        modal.show({
            title: 'הזזת מפגש',
            body: (
                <div className='flex flex-col gap-4'>
                    <div className='flex flex-col items-center'>
                        <span>
                            בחר/י תאריך ושעה חדשים למפגש <br />
                        </span>
                        <label className='line-through text-center mx-auto'>{format(session.time, 'dd/MM/yy hh:mm')}</label>
                    </div>
                    <input
                        className='p-1'
                        type='datetime-local'
                        onChange={(e) => {
                            new_time = new Date(e.target.value);
                            console.log('new_time', new_time);
                        }}
                    />
                    <button
                        className='px-2 py-1 bg-blue-500 hover:bg-blue-600 rounded-lg font-medium text-white disabled:pointer-events-none disabled:opacity-20'
                        onClick={() => {
                            if (new_time) handleRescheduleSession(session_id, new_time);
                        }}>
                        הזזת פגישה
                    </button>
                </div>
            ),
        });
    };
    const maskedName = `${session.first_name?.slice(0, 10)} ${session.last_name?.slice(0, 1) + '.'}`;
    const member = session.member;
    const service = session.service;
    const moreInfo = [
        { title: 'שם', value: `${session.first_name} ${session.last_name}` },
        { title: 'מייל', value: session?.email },
        { title: 'תאריך', value: `${moment(session.time).format('DD/MM HH:mm')} (${moment(session.time).fromNow()})` },
        { title: 'טלפון', value: <span dir='ltr'> {session?.phone}</span> },
        { title: 'מסובסד', value: session?.subsidized ? 'כן' : 'לא' },
        { title: 'שיחת המשך', value: session.followup_of ? 'כן' : 'לא' },
        { title: 'משך', value: `${session.duration} דקות` },

        {
            title: 'סטטוס',
            value: <span className={`font-bold ${session.payment_confirm ? 'text-green-700' : 'text-red-700'}`}>{session.payment_confirm !== true ? 'ממתין לתשלום' : 'שולם'}</span>,
        },
    ];
    const controls = ({ hideInfoButton }: { hideInfoButton?: boolean }) => (
        <div className='flex flex-col justify-center'>
            <div className='flex flex-row gap-2'>
                {session?.coupon &&  <IconButton
                    onClick={() => {
                        const coupon = typeof session.coupon === 'string' 
                            ? null 
                            : session.coupon;
                        
                        if (!coupon) return;
                        
                        const discountDisplay = coupon.discountType === 'fixed' 
                            ? `${coupon.discountValue} ₪`
                            : `${coupon.discountValue}%`;
                        
                        modal.show({
                            title: 'פרטי קופון',
                            body: (
                                <div dir='rtl' className='flex flex-col gap-3 p-4'>
                                    <div className='flex flex-row justify-between items-center border-b pb-2'>
                                        <span className='font-semibold'>קוד קופון:</span>
                                        <span className='font-bold text-lg'>{coupon.code}</span>
                                    </div>
                                    <div className='flex flex-row justify-between items-center border-b pb-2'>
                                        <span className='font-semibold'>סוג הנחה:</span>
                                        <span>{coupon.discountType === 'fixed' ? 'סכום קבוע' : 'אחוז'}</span>
                                    </div>
                                    <div className='flex flex-row justify-between items-center border-b pb-2'>
                                        <span className='font-semibold'>ערך ההנחה:</span>
                                        <span className='font-bold text-green-600'>{discountDisplay}</span>
                                    </div>
                                </div>
                            ),
                        });
                    }}
                    color='info'
                    title='פרטי קופון'>
                    <LocalOffer />
                </IconButton> }
               
                {(session as any)?.memberQuestionsToExpert && (
                    <IconButton
                        onClick={() => {
                            const questionsData = (session as any).memberQuestionsToExpert;
                            modal.show({
                                title: 'שאלות מהמשתמש',
                                body: (
                                    <div dir='rtl' className='flex flex-col gap-4 p-4'>
                                        {questionsData?.data && Array.isArray(questionsData.data) && questionsData.data.length > 0 ? (
                                            questionsData.data.map((item: any, index: number) => (
                                                <div key={item._id || index} className='border-b pb-3 last:border-b-0'>
                                                    <div className='font-semibold mb-2 text-blue-700'>שאלה {index + 1}:</div>
                                                    <div className='mb-2'>{item.question}</div>
                                                    <div className='font-semibold mb-1 text-green-700'>תשובה:</div>
                                                    <div className='text-gray-700'>{item.answer}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div>אין שאלות להצגה</div>
                                        )}
                                    </div>
                                ),
                            });
                        }}
                        color='primary'
                        title='שאלות מהמשתמש'>
                        <QuestionAnswer />
                    </IconButton>
                )}
               
                {!hideInfoButton && (
                    <IconButton
                        onClick={() => {
                            modal.show({
                                title: `${service.title.he} עם ${maskedName}`,
                                body: (
                                    <table dir='rtl' className='border-spacing-x-4 border-spacing-y-2 border-separate w-full'>
                                        {moreInfo.map((row) => (
                                            <tr key={row.title}>
                                                <td>{`${row.title}:`}</td>
                                                <td className='font-semibold'>{row.value}</td>
                                            </tr>
                                        ))}
                                    </table>
                                ),
                                footer: <div className='flex flex-row justify-left w-full'>{controls({ hideInfoButton: true })}</div>,
                            });
                        }}
                        color='primary'
                        title='מידע נוסף'>
                        <InfoSharp />
                    </IconButton>
                )}



           {session?.service?.price > 0 && session?.host == "62f4da77454b14d740800a37" &&  <IconButton
                    onClick={() => {

                      
                     modal.show({
                     
                     title: 'יצירת פגישות',
                     body: (
                        <div className='flex flex-col gap-4 p-4 w-full max-w-2xl min-h-96 min-w-96' style={{minWidth:'460px'}} dir='rtl'>
                            <div className='flex flex-col gap-2'>
                                <label className='font-semibold'>פגישות:</label>
                                <div id="meetings-container" className='flex flex-col gap-3'>
                                    {/* Meeting entries will be added here dynamically */}
                                </div>
                                <button 
                                    className='px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm w-full'
                                    onClick={() => {
                                        const container = document.getElementById('meetings-container');
                                        if (container) {
                                            const meetingIndex = container.children.length;
                                            const meetingDiv = document.createElement('div');
                                            meetingDiv.className = 'border border-gray-200 rounded-md p-3 bg-gray-50';
                                            meetingDiv.innerHTML = `
                                                <div class="flex items-center justify-between mb-2" style="background:#fff;padding:20px">
                                                    <span class="font-medium">פגישה ${meetingIndex + 1}</span>
                                                    <button style="line-height:0" class="text-red-500 hover:text-red-700 text-sm" onclick="this.parentElement.parentElement.remove()">
                                                        ✕
                                                    </button>
                                                </div>
                                                <div class="flex flex-col gap-2">
                                                    <div>
                                                        <label class="text-sm font-medium">תאריך ושעה:</label>
                                                        <input 
                                                            type="datetime-local"
                                                            class="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                            min={addMinutes(new Date(), 20).toISOString().slice(0,16)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label class="text-sm font-medium">קופון (אופציונלי):</label>
                                                        <input type="text" class="w-full p-2 border border-gray-300 rounded-md text-sm" placeholder="הכנס קוד קופון" />
                                                    </div>
                                                </div>
                                            `;
                                            container.appendChild(meetingDiv);
                                        }
                                    }}
                                >
                                    + הוספת פגישה
                                </button>
                            </div>
                            
                            <div className='flex gap-2 mt-4'>
                                <button 
                                    className='px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600'
                                    onClick={async (event) => {
                                        const container = document.getElementById('meetings-container');
                                        if (container) {
                                            const meetings: Array<{
                                                follow_up_accepted: boolean;
                                                member_id: string;
                                                service_id: string;
                                                time: string;
                                                coupon: string;
                                            }> = [];
                                            const meetingDivs = container.querySelectorAll('.border.border-gray-200.rounded-md');
                                            
                                            // Check if there are any meetings
                                            if (meetingDivs.length === 0) {
                                                alert?.showAlert('לא הוספת פגישות', 'error');
                                                return;
                                            }
                                            
                                            // Check if all meetings have dates and are not in past/today
                                            let hasEmptyDate = false;
                                            let hasPastDate = false;
                                            const tomorrow = new Date();
                                            tomorrow.setHours(0, 0, 0, 0);
                                            tomorrow.setDate(tomorrow.getDate() + 1);

                                            for (let index = 0; index < meetingDivs.length; index++) {
                                                const meetingDiv = meetingDivs[index];
                                                const dateInput = meetingDiv.querySelector('input[type="datetime-local"]') as HTMLInputElement;
                                                
                                                if (!dateInput?.value) {
                                                    hasEmptyDate = true;
                                                    break;
                                                }

                                                const meetingDate = new Date(dateInput.value);
                                                if (meetingDate < tomorrow) {
                                                    hasPastDate = true;
                                                    break;
                                                }
                                            }
                                            
                                            if (hasEmptyDate) {
                                                alert?.showAlert('כל הפגישות חייבות לכלול תאריך ושעה', 'error');
                                                return;
                                            }

                                            if (hasPastDate) {
                                                alert?.showAlert('לא ניתן לקבוע פגישות להיום או לתאריכים שעברו', 'error');
                                                return;
                                            }
                                            
                                            // Show loading state
                                            const button = event.target as HTMLButtonElement;
                                            const originalText = button.textContent;
                                            button.textContent = 'יוצר פגישות...';
                                            button.disabled = true;
                                            
                                            // Create full-screen loader overlay
                                            const loaderOverlay = document.createElement('div');
                                            loaderOverlay.style.cssText = `
                                                position: fixed;
                                                top: 0;
                                                left: 0;
                                                width: 100%;
                                                height: 100%;
                                                background: rgba(255, 255, 255, 0.9);
                                                display: flex;
                                                flex-direction: column;
                                                justify-content: center;
                                                align-items: center;
                                                z-index: 9999;
                                                font-size: 18px;
                                                font-weight: bold;
                                            `;
                                            loaderOverlay.innerHTML = `
                                                <div style="text-align: center;">
                                                    <div style="width: 50px; height: 50px; border: 5px solid #f3f3f3; border-top: 5px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 20px;"></div>
                                                    <div>יוצר פגישות...</div>
                                                </div>
                                                <style>
                                                    @keyframes spin {
                                                        0% { transform: rotate(0deg); }
                                                        100% { transform: rotate(360deg); }
                                                    }
                                                </style>
                                            `;
                                            document.body.appendChild(loaderOverlay);
                                            
                                            try {
                                                for (let index = 0; index < meetingDivs.length; index++) {
                                                    const meetingDiv = meetingDivs[index];
                                                    const durationInput = meetingDiv.querySelector('input[type="number"]') as HTMLInputElement;
                                                    const dateInput = meetingDiv.querySelector('input[type="datetime-local"]') as HTMLInputElement;
                                                    const couponInput = meetingDiv.querySelector('input[type="text"]') as HTMLInputElement;
                                                    
                                                    const meetingData = {
                                                       
                                                        follow_up_accepted:false,
                                                        is_future:true,
                                                        member_id:  session.member?._id,
                                                        service_id:  session.service?._id,
                                                        time: dateInput?.value || '',
                                                        coupon: couponInput?.value || ''
                                                    };

                                                    // Make API call to create private sessions
                                                    try {
                                                        const response = await axios.post(`${constants.API_URL}/instructor/admin/private_session`, meetingData);
                                                        console.log('Private session created:', response.data);
                                                    } catch (error) {
                                                        console.error('Error creating private session:', error);
                                                    }
                                                    
                                                    meetings.push(meetingData);
                                                    console.log(`Meeting ${index + 1}:`, meetingData);
                                                }
                                                
                                                console.log('All meetings:', meetings);
                                                alert?.showAlert('הפגישות נוצרו בהצלחה', 'success');
                                                modal.hide();
                                            } finally {
                                                // Restore button state
                                                button.textContent = originalText;
                                                button.disabled = false;
                                                // Remove loader overlay
                                                document.body.removeChild(loaderOverlay);
                                            }
                                        }
                                    }}
                                >
                                    יצירת פגישות
                                   </button>
                                <button 
                                    className='px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400'
                                    onClick={() => modal.hide()}
                                >
                                    ביטול
                                </button>
                            </div>
                        </div>
                     )
                     })
                    }}
                    color='info'
                    disabled={false}
                    title='יצירת כמה פגישות'>
                    <Schedule />
                </IconButton> }
               


                {/* Wellness (תקציב ניהולי) sessions are booked ONLY through the company-budget
                    offer flow — a manually-scheduled one isn't tied to an offer, so it wouldn't
                    draw down the company budget yet the expert would still be paid. So hide BOTH
                    scheduling actions (existing single + new future) on wellness services. */}
                {!session?.service?.wellness_session && (
                    <IconButton
                        onClick={() => {
                            onPrivateSessionSetAppointment(session.member?._id, session?.first_name, session.service);
                        }}
                        color='info'
                        disabled={!session.schedule_another}
                        title='פגישה נוספת'>
                        <EventRepeatTwoTone />
                    </IconButton>
                )}
                {/* NEW (GH-539/625): schedule several FUTURE appointments at once (min 2,
                    deferred payment). Separate from the single "פגישה נוספת" above; shown only
                    to experts in the gradual-rollout list, and hidden on wellness services
                    (see above). session.host is the logged-in expert (own sessions only). The
                    actual per-session price is resolved at T-48h from the member's state. */}
                {constants.FUTURE_APPOINTMENTS_EXPERT_IDS.includes(session?.host) && !session?.service?.wellness_session && (
                    <IconButton
                        onClick={() => {
                            onScheduleFutureAppointments(session.member?._id, session?.first_name, session.service);
                        }}
                        color='info'
                        title='קביעת כמה פגישות עתידיות'>
                        <Schedule />
                    </IconButton>
                )}
                <IconButton
                    onClick={() => {
                        showRescheduleModal(session._id, session);
                    }}
                    color='info'
                    disabled={!session.cancellable}
                    title='הזזת מפגש'>
                    <Edit />
                </IconButton>
                <IconButton
                    disabled={!session.cancellable}
                    onClick={() => {
                        showCancelDialog(session._id);
                    }}
                    color='warning'
                    title='ביטול פגישה'>
                    <Delete />
                </IconButton>
            </div>
        </div>
    );
    return (
        <>
            <div
                className={twMerge(
                    `first:border-t w-full transition-all text-right flex flex-row justify-between px-4 border-b py-1.5`,
                    moment(session.time).isAfter(new Date()) ? 'bg-gradient-to-l from-green-100 to-white hover:to-green-50' : 'hover:bg-gray-100',
                    isLoading ? 'animate-pulse' : ''
                )}>
                <div className='flex  flex-col gap-1 w-1/2'>
                    <div className='flex flex-row gap-6 items-center'>
                        <button
                            type='button'
                            className='font-semibold text-lg text-right hover:underline hover:text-blue-700 cursor-pointer bg-transparent border-0 p-0'
                            title='כל הפגישות עם המטופל/ת'
                            onClick={() => onShowMemberSessions(session)}>
                            {maskedName}
                        </button>
                        {!session.payment_confirm ? <span className='text-red-700 font-bold'>טרם אושר</span> : null}
                    </div>
                    <div className='flex flex-row gap-x-8 justify-between text-sm'>
                        <span className='font-semibold'>{moment(session.time).format('DD/MM/YY HH:mm')}</span>
                        <span className='text-right flex flex-1'>{`${service?.title?.he} ${session.followup_of ? '(פגישת המשך)' : ''}`}</span>
                        <span style={{ minWidth: 60, whiteSpace: 'nowrap' }}>{`${session?.price ? `${session.price || 0} ₪` : ''}`}</span>
                        <span style={{ minWidth: 160, whiteSpace: 'nowrap' }}>{session?.member?._id ? 'מנוי/ת GroupHug' : ''}</span>
                    </div>
                </div>
                <div style={{ minWidth: 110 }} className='flex flex-col items-center justify-center gap-0.5'>
                    {pairSessionNumber != null && (
                        <>
                            <span
                                className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                                    pairSessionNumber <= 7
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-orange-100 text-orange-700'
                                }`}
                                title={[
                                    pairSessionNumber <= 7 ? 'טיפול מסובסד' : 'מחיר מלא',
                                    (session as any).cycle_start_date
                                        ? `מחזור הטבה מ-${moment((session as any).cycle_start_date).format('DD/MM/YY')}`
                                        : null,
                                ]
                                    .filter(Boolean)
                                    .join(' — ')}
                            >
                                {`טיפול ${pairSessionNumber}/7`}
                            </span>
                            {/* GH-873: the "מודל חדש" badge is gone — since 20/05/2026 every subsidized session is on that model; the cycle caption stays */}
                            {(session as any).cycle_start_date && (
                                <span className='text-[10px] text-gray-500 whitespace-nowrap'>
                                    {`מחזור ${moment((session as any).cycle_start_date).format('DD/MM/YY')}`}
                                </span>
                            )}
                        </>
                    )}
                </div>
                {controls({})}
            </div>
        </>
    );
};

type PrivateSessionsTableProps = {
    privateSessions?: IPrivateServiceAppointment[];
    onPrivateSessionSetAppointment: (member_id: string, member_name: string, service: IPrivateService) => void;
    onScheduleFutureAppointments: (member_id: string, member_name: string, service: IPrivateService) => void;
    isPsychologist?: boolean;
    refetchFunction: () => Promise<void>;
    loading: boolean;
};
// GH-715: one key per member so all their sessions group together — GroupHug members by id,
// externally-booked ones (no member record) by email.
const memberKey = (s: IPrivateServiceAppointment) => s.member?._id || s.email || '';
const maskName = (s: IPrivateServiceAppointment) => `${s.first_name?.slice(0, 10)} ${s.last_name?.slice(0, 1) + '.'}`;

const PrivateSessionsTable = ({ privateSessions = [], onPrivateSessionSetAppointment, onScheduleFutureAppointments, refetchFunction }: PrivateSessionsTableProps) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const modal = useModal();

    // GH-715 (Yulia): search a member by name / email — filters the list client-side (the endpoint
    // returns the expert's full history, paging is local).
    const [search, setSearch] = useState('');
    const query = search.trim().toLowerCase();
    const visibleSessions = query
        ? privateSessions.filter((s) =>
              `${s.first_name || ''} ${s.last_name || ''} ${s.email || ''}`.toLowerCase().includes(query)
          )
        : privateSessions;

    // GH-715: popup with EVERY session of one member, oldest → newest.
    const showMemberSessions = (target: IPrivateServiceAppointment) => {
        const key = memberKey(target);
        const rows = privateSessions
            .filter((s) => memberKey(s) === key)
            .sort((a, b) => moment(a.time).diff(b.time));
        const upcoming = rows.filter((s) => moment(s.time).isAfter(new Date())).length;
        modal.show({
            title: `הפגישות עם ${maskName(target)}`,
            body: (
                <div dir='rtl' className='flex flex-col gap-2'>
                    <div className='text-sm text-gray-600'>
                        {rows.length} פגישות{upcoming ? `, מתוכן ${upcoming} עתידיות` : ''} · מהישנה לחדשה
                    </div>
                    <div className='flex flex-col divide-y max-h-[60vh] overflow-y-auto'>
                        {rows.map((s, i) => {
                            const future = moment(s.time).isAfter(new Date());
                            return (
                                <div
                                    key={s._id}
                                    className={twMerge('flex flex-row items-center gap-3 py-2 text-sm', future ? 'bg-green-50' : '')}>
                                    <span className='text-gray-400 w-6 text-center'>{i + 1}</span>
                                    <span className={twMerge('font-semibold whitespace-nowrap', future ? 'text-green-700' : '')}>
                                        {moment(s.time).format('DD/MM/YY HH:mm')}
                                    </span>
                                    <span className='flex-1'>
                                        {s.service?.title?.he}
                                        {s.followup_of ? ' (פגישת המשך)' : ''}
                                    </span>
                                    {s.subsidized ? <span className='text-xs text-blue-700'>מסובסד</span> : null}
                                    <span className='whitespace-nowrap'>{s.price ? `${s.price} ₪` : ''}</span>
                                    <span className={twMerge('text-xs whitespace-nowrap', s.payment_confirm ? 'text-green-700' : 'text-red-700')}>
                                        {s.payment_confirm ? 'שולם' : 'ממתין לתשלום'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ),
            footer: (
                <div className='flex flex-row justify-left w-full'>
                    <Button variant='outlined' size='small' onClick={() => modal.hide()}>
                        סגירה
                    </Button>
                </div>
            ),
        });
    };

    const showNewSubsidyUI = new Date() >= constants.SUBSIDY_2026_START;

    // Compute per-(member, therapist) pair session number for the new subsidy model.
    // Sort by booking order (createdAt) so the number aligns with the `subsidized`
    // flag — which is set at booking time based on the pair count at that moment.
    // Counter resets per cycle (grouped by cycle_start_date) so reset gives a clean slate.
    const pairSessionMap = new Map<string, number>();
    if (showNewSubsidyUI) {
        const nonCancelled = privateSessions
            .filter((s) => (s as any).active !== false)
            .sort((a, b) => moment((a as any).createdAt).diff((b as any).createdAt));

        const counters: Record<string, number> = {};
        for (const s of nonCancelled) {
            // The "טיפול X/7" badge is meaningful only for SAFE_SPACE pair sessions.
            // Skip 1TO1 / 1TO1_SERIES / SAFE_SPACE_TRIAL etc. so non-therapist experts
            // (lecturers, 1:1 coaches) don't see a misleading therapy counter on
            // their rows.
            if ((s as any).type !== 'SAFE_SPACE') continue;
            const memberId = s.member?._id || s.email;
            if (!memberId) continue;
            // Group by effective_cycle_start_date (backend sets this with a fallback
            // to the company reset for legacy in-cycle sessions). cycle_start_date
            // itself stays untouched and marks new-model sessions only.
            const groupingCycle =
                (s as any).effective_cycle_start_date || (s as any).cycle_start_date;
            const cycleKey = groupingCycle ? new Date(groupingCycle).toISOString() : 'no-cycle';
            const key = `${memberId}:${cycleKey}`;
            counters[key] = (counters[key] || 0) + 1;
            pairSessionMap.set(s._id, counters[key]);
        }
    }

    return (
        <div>
            <div className='px-4 flex flex-col gap-1 mb-2'>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>לקבלת פרטים נוספים על הפגישה לחצו על </span>
                    <InfoSharp color={'primary'} />
                    <span>בשורה המתאימה</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>להזזת פגישה שנקבעה לחצו על </span>
                    <Edit color={'info'} />
                    <span>בשורה המתאימה</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>לקביעת פגישה חוזרת לחצו על </span>
                    <EventRepeatTwoTone color={'info'} />
                    <span>בשורה המתאימה</span>
                </div>
                <div>
                    <span>מפגשים עתידיים מסומנים</span>
                    <span className='text-green-600 font-bold'>{' בצבע ירוק'}</span>
                </div>
                {showNewSubsidyUI && (
                    <div className='text-xs text-gray-600 text-left'>
                        <div>* מספר הפגישה בזוג נקבע לפי מועד קביעתה,</div>
                        <div>לא לפי מועד הפגישה עצמה.</div>
                    </div>
                )}
            </div>
            {/* GH-715: member search (name / email) */}
            <div className='px-4 mb-2 flex flex-row items-center gap-3'>
                <TextField
                    size='small'
                    placeholder='חיפוש מטופל/ת לפי שם או אימייל'
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                    inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon fontSize='small' />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position='end'>
                                <IconButton size='small' onClick={() => { setSearch(''); setPage(0); }} title='ניקוי'>
                                    <Clear fontSize='small' />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{ minWidth: 320 }}
                />
                {query ? (
                    <span className='text-sm text-gray-600'>
                        {visibleSessions.length ? `${visibleSessions.length} פגישות` : 'לא נמצאו פגישות'}
                    </span>
                ) : null}
            </div>
            <div>
                <div>
                    <div className='w-full flex flex-col gap-1'>
                        {[...visibleSessions]
                            .sort((a, b) => moment(b.time).diff(a.time))
                            .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                            .map((session) => (
                                <Row
                                    key={`session#${session?._id}`}
                                    session={session}
                                    onPrivateSessionSetAppointment={onPrivateSessionSetAppointment}
                                    onScheduleFutureAppointments={onScheduleFutureAppointments}
                                    refetchFunction={refetchFunction}
                                    pairSessionNumber={pairSessionMap.get(session._id)}
                                    onShowMemberSessions={showMemberSessions}
                                />
                            ))}
                    </div>

                    <TablePagination
                        backIconButtonProps={{ style: { transform: 'rotate(180deg)' } }}
                        nextIconButtonProps={{ style: { transform: 'rotate(180deg)' } }}
                        component='div'
                        count={visibleSessions.length}
                        page={page}
                        onPageChange={(e, newPage) => {
                            setPage(newPage);
                        }}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default PrivateSessionsTable;
